<script setup lang="ts">
/**
 * Compact sidebar widget for recently opened items across all models.
 * Shows the newest few entries (icon, label, type badge, relative time) with
 * a header counter and a "view all" trigger opening the AllRecentsSheet.
 * Max visible items configurable via SessionSettings (recentHistoryMaxItems).
 */
import { Link } from '@inertiajs/vue3';
import { ChevronRight } from '@lucide/vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { getSessionSettings } from '@ja/portal-query';

import { OPEN_ALL_RECENTS_EVENT, useRecentHistory } from '../composables/useRecentHistory';
import { useRecentItemDisplay } from '../composables/useRecentItemDisplay';
import type { RecentHistoryItem } from '../types';
import { edit } from '../wayfinder/routes';

import AllRecentsSheet from './AllRecentsSheet.vue';

interface Props {
    /** Maximum number of items to display (overrides SessionSettings) */
    maxItems?: number;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { allRecentItems } = useRecentHistory();
const { modelIcon, typeLabel, relativeTime } = useRecentItemDisplay();

const effectiveMaxItems = computed(() => {
    if (props.maxItems !== undefined) return props.maxItems;
    const settings = getSessionSettings();
    return settings.recentHistoryMaxItems ?? 3;
});

const visibleItems = computed(() => allRecentItems.value.slice(0, effectiveMaxItems.value));
const hasItems = computed(() => allRecentItems.value.length > 0);

const sheetOpen = ref(false);

// Global open hook — any page can surface the sheet via openAllRecents()
function openSheet(): void {
    sheetOpen.value = true;
}

onMounted(() => window.addEventListener(OPEN_ALL_RECENTS_EVENT, openSheet));
onBeforeUnmount(() => window.removeEventListener(OPEN_ALL_RECENTS_EVENT, openSheet));

function itemHref(item: RecentHistoryItem): string {
    return edit({ model: item.model, id: item.record_id }).url;
}
</script>

<template>
    <div v-if="hasItems" class="px-2">
        <!-- Header: count + "view all" trigger -->
        <div class="flex items-center justify-between px-2 pb-1">
            <span class="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                {{ t('crud.recents_count', allRecentItems.length) }}
            </span>
            <button
                type="button"
                class="text-primary hover:text-primary/80 flex items-center gap-0.5 text-xs font-medium transition-colors"
                @click="sheetOpen = true"
            >
                {{ t('crud.view_all') }}
                <ChevronRight class="size-3" />
            </button>
        </div>

        <!-- Newest items -->
        <div class="space-y-0.5">
            <Link
                v-for="item in visibleItems"
                :key="item.id"
                :href="itemHref(item)"
                class="hover:bg-muted flex items-center gap-3 rounded-lg px-2 py-1.5"
            >
                <div
                    :class="[
                        'flex size-8 shrink-0 items-center justify-center rounded-lg',
                        item.is_draft ? 'bg-amber-500/15 text-amber-500' : 'bg-muted text-muted-foreground',
                    ]"
                >
                    <component :is="modelIcon(item)" class="size-4" />
                </div>
                <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-medium">
                        {{ item.is_draft ? t('crud.draft') : item.label }}
                    </div>
                    <span
                        :class="[
                            'mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium capitalize',
                            item.is_draft ? 'bg-amber-500/15 text-amber-500' : 'bg-muted text-muted-foreground',
                        ]"
                    >
                        {{ item.badge ?? typeLabel(item) }}
                    </span>
                </div>
                <span class="text-muted-foreground/75 shrink-0 whitespace-nowrap text-[10px]">
                    {{ relativeTime(item.opened_at) }}
                </span>
            </Link>
        </div>

        <AllRecentsSheet v-model:open="sheetOpen" />
    </div>
</template>
